import dayjs from "dayjs";
import { MatrialFilterFormValuesPaginated } from "features/master/components/master-filter-form";
import { UpLoadingLogTable } from "features/unloading-log/component/table/Uploading-Item";
import { UpLoadingLogViewTopBar } from "features/unloading-log/component/top-bar/UpLoadingView-top-bar";
import { useGetUpLoadingList } from "features/unloading-log/use-cases/get-getUpLoading";
import { omit } from "lodash";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { formatDate } from "utils/date";

const PAGE_SIZE_KEY = "unloading_log_page_size";

export const UnloadingLog = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showAppUserModal, setShowAppUserModal] = useState(false);

  // -------------------- URL → params --------------------
  const params: any = useMemo(() => {
    const obj = Object.fromEntries(searchParams.entries());
    const allDates = searchParams.getAll("date");

    return {
      page: Number(obj.page) || 1,
      size:
        Number(obj.size) ||
        Number(localStorage.getItem(PAGE_SIZE_KEY)) ||
        10,
      DriverName: obj.DriverName || undefined,
      SupplierName: obj.SupplierName || undefined,
      VehicleNumber: obj.VehicleNumber || undefined,
      MaterialName: obj.MaterialName || undefined,
      Status: obj.Status || undefined,
      date: allDates.length
        ? allDates.filter((d) => d && d !== "undefined")
        : undefined,
    } as any;
  }, [searchParams]);

  // -------------------- Prepare API payload --------------------
  const preparePayload = (p: MatrialFilterFormValuesPaginated) => {
    const [start, end] = Array.isArray(p.date) ? p.date : [];

    const startDate = start ? dayjs(start) : null;
    const endDate = end ? dayjs(end) : null;

    return {
      ...omit(p, ["date"]),
      EntryTypeId: 2,
      UnloadingStartDate:
        startDate && startDate.isValid()
          ? formatDate({
              date: startDate.startOf("day"),
              format: "dbDate",
            })
          : undefined,
      UnloadingEndDate:
        endDate && endDate.isValid()
          ? formatDate({
              date: endDate.endOf("day"),
              format: "dbDate",
            })
          : undefined,
    };
  };

  const payload = useMemo(() => preparePayload(params), [params]);

  // -------------------- API call --------------------
  const getvehcListQueryResponse: any = useGetUpLoadingList(payload, {
    retry: false,
  });

  // -------------------- Handle filters --------------------
  const handleFiltersChange = (values: any) => {
    const filteredValues = Object.fromEntries(
      Object.entries(values).filter(([, v]) => v !== undefined && v !== "")
    );

    const search: Record<string, string | string[]> = {
      ...filteredValues,
      page: "1",
      size: params.size.toString(),
    };

    // ✅ Serialize date to URL
    if (values.date && Array.isArray(values.date)) {
      search.date = values.date.map((d: dayjs.Dayjs) =>
        d.toISOString()
      );
    }

    setSearchParams(search);
  };

  // -------------------- Handle table pagination --------------------
  const handleTableChange = (newParams: { page: number; size: number }) => {
    if (newParams.size) {
      localStorage.setItem(PAGE_SIZE_KEY, newParams.size.toString());
    }

    const currentFilters = Object.fromEntries(
      Object.entries(params).filter(
        ([, v]) => v !== undefined && v !== ""
      )
    );

    setSearchParams({
      ...currentFilters,
      page: newParams.page.toString(),
      size: newParams.size.toString(),
    });
  };

  // -------------------- Initial filter values (🔥 FIXED) --------------------
  const getInitialFilterValues = {
    DriverName: params.DriverName,
    SupplierName: params.SupplierName,
    VehicleNumber: params.VehicleNumber,
    MaterialName: params.MaterialName,
    Status: params.Status,

    // ✅ CONVERT STRING → dayjs[]
    date: Array.isArray(params.date)
      ? params.date.map((d: any) => dayjs(d))
      : undefined,
  };

  return (
    <>
      <UpLoadingLogViewTopBar
        getInitialFilterValues={getInitialFilterValues}
        handleFilter={handleFiltersChange}
        showAppUserModal={showAppUserModal}
        setShowAppUserModal={setShowAppUserModal}
        onRefresh={getvehcListQueryResponse.refetch}
      />

      <UpLoadingLogTable
        isListUpLoading={getvehcListQueryResponse.isFetching}
        UploadingList={getvehcListQueryResponse?.data?.res?.items}
        itemsCount={getvehcListQueryResponse.data?.totalRecords || 0}
        tableParams={params as any}
        updateParams={handleTableChange as any}
        onRefresh={getvehcListQueryResponse.refetch}
        isFetching={getvehcListQueryResponse.isFetching}
      />
    </>
  );
};
